export interface TimezoneOption {
  label: string
  value: string
  region: string
  searchTerms: string
}

const getIANATimezones = (): string[] => {
  try {
    return Intl.supportedValuesOf('timeZone')
  } catch {
    return FALLBACK_TIMEZONES
  }
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'America/Toronto',
  'America/Vancouver',
  'Europe/London',
  'Europe/Paris',
  'Europe/Berlin',
  'Europe/Madrid',
  'Asia/Tokyo',
  'Asia/Shanghai',
  'Asia/Kolkata',
  'Asia/Dubai',
  'Asia/Singapore',
  'Africa/Cairo',
  'Africa/Johannesburg',
  'Africa/Lagos',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Perth',
  'Australia/Brisbane',
  'Pacific/Auckland',
  'Pacific/Fiji',
]

const getRegionFromTimezone = (tz: string): string => {
  if (tz.startsWith('America/')) {
    return 'Americas'
  }
  if (tz.startsWith('Europe/')) {
    return 'Europe'
  }
  if (tz.startsWith('Asia/')) {
    return 'Asia'
  }
  if (tz.startsWith('Africa/')) {
    return 'Africa'
  }
  if (tz.startsWith('Pacific/')) {
    return 'Pacific'
  }
  if (tz.startsWith('Australia/') || tz.startsWith('Antarctica/') || tz.startsWith('Indian/')) {
    return 'Oceania'
  }
  return 'Other'
}

const getTimezoneLongName = (tz: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'long',
    })
    const parts = formatter.formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value || tz
  } catch {
    return tz
  }
}

export const getCurrentOffset = (tz: string): string => {
  try {
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: tz,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(new Date())
    return parts.find((p) => p.type === 'timeZoneName')?.value || ''
  } catch {
    return ''
  }
}

const getDisplayName = (tz: string): string => {
  return tz.split('/').pop()?.replace(/_/g, ' ') || tz
}

const buildSearchTerms = (tz: string, longName: string): string => {
  return `${tz.toLowerCase().replace(/[/_]/g, ' ')} ${longName.toLowerCase()}`
}

let _cachedOptions: TimezoneOption[] | null = null

export const getAllTimezones = (): TimezoneOption[] => {
  if (_cachedOptions) {
    return _cachedOptions
  }

  const ianaTimezones = getIANATimezones()

  _cachedOptions = ianaTimezones
    .map((tz) => {
      const longName = getTimezoneLongName(tz)
      const displayName = getDisplayName(tz)

      return {
        label: `${displayName} \u2014 ${longName}`,
        value: tz,
        region: getRegionFromTimezone(tz),
        searchTerms: buildSearchTerms(tz, longName),
      }
    })
    .sort((a, b) => {
      if (a.region !== b.region) {
        return a.region.localeCompare(b.region)
      }
      return a.label.localeCompare(b.label)
    })

  return _cachedOptions
}

export const POPULAR_TIMEZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Perth',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Asia/Singapore',
  'Pacific/Auckland',
]

export const LEGACY_TIMEZONE_MAP: Record<string, string> = {
  EST: 'America/New_York',
  CST: 'America/Chicago',
  MST: 'America/Denver',
  PST: 'America/Los_Angeles',
  AEST: 'Australia/Sydney',
  AEDT: 'Australia/Sydney',
  AWST: 'Australia/Perth',
  ACST: 'Australia/Adelaide',
  NZST: 'Pacific/Auckland',
  GMT: 'Europe/London',
  BST: 'Europe/London',
  CET: 'Europe/Paris',
  IST: 'Asia/Kolkata',
  SGT: 'Asia/Singapore',
  JST: 'Asia/Tokyo',
}

const IANA_SET = new Set(getIANATimezones())
// 'UTC' is accepted by Intl.DateTimeFormat but may not appear in
// Intl.supportedValuesOf('timeZone') which uses the canonical 'Etc/UTC'.
IANA_SET.add('UTC')
// Ensure all legacy map targets are recognised as valid
for (const tz of Object.values(LEGACY_TIMEZONE_MAP)) {
  IANA_SET.add(tz)
}
export const VALID_TIMEZONES = IANA_SET

export const validateTimezone = (timezone: string): boolean => {
  return IANA_SET.has(timezone)
}

export const resolveTimezone = (tz: string | null | undefined): string => {
  if (!tz) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  }
  if (IANA_SET.has(tz)) {
    return tz
  }
  const mapped = LEGACY_TIMEZONE_MAP[tz.toUpperCase()]
  if (mapped) {
    return mapped
  }
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/**
 * Parses a GMT offset string like "GMT+10", "GMT-5", "GMT+5:30" into total minutes.
 * Used for sorting timezones numerically by offset (-720 to +840).
 */
const parseOffsetMinutes = (offset: string): number => {
  const match = offset.match(/GMT([+-])(\d+)(?::(\d+))?/)
  if (!match) {
    return 0
  }
  const sign = match[1] === '+' ? 1 : -1
  const hours = parseInt(match[2] as string, 10)
  const minutes = parseInt((match[3] ?? '0') as string, 10)
  return sign * (hours * 60 + minutes)
}

/**
 * Returns a flat list of timezone options suitable for the Dropdown component.
 * Popular timezones (Hapana core markets) are prepended at the top with a divider.
 * The remaining timezones are sorted by UTC offset (−12 → +14), then alphabetically
 * by city name within the same offset — consistent with how the offset is displayed
 * in the label and matching common business app conventions (Windows, Zoom, etc.).
 */
export const getTimezoneDropdownOptions = (): Array<{
  label: string
  value: string
  hasDivider?: boolean
}> => {
  const allTimezones = getAllTimezones()

  const popularOptions = POPULAR_TIMEZONES.map((tzValue) => {
    const option = allTimezones.find((opt) => opt.value === tzValue)
    if (!option) {
      return null
    }
    const offset = getCurrentOffset(tzValue)
    return {
      label: `${offset} ${option.label}`,
      value: option.value,
    }
  }).filter(Boolean) as Array<{ label: string; value: string }>

  const lastPopularIndex = popularOptions.length - 1
  const popularWithDivider = popularOptions.map((opt, i) => ({
    ...opt,
    hasDivider: i === lastPopularIndex,
  }))

  const allWithOffset = allTimezones.map((tz) => {
    const offset = getCurrentOffset(tz.value)
    return {
      label: `${offset} ${tz.label}`,
      value: tz.value,
      offsetMinutes: parseOffsetMinutes(offset),
    }
  })

  const hasUtc = allWithOffset.some((o) => o.value === 'UTC' || o.value === 'Etc/UTC')
  const utcOffset = getCurrentOffset('UTC') || 'GMT+0'
  const utcOption = {
    label: `${utcOffset} UTC — Coordinated Universal Time`,
    value: 'UTC',
    offsetMinutes: 0,
  }
  const withUtc = hasUtc ? allWithOffset : [utcOption, ...allWithOffset]

  const sorted = withUtc.sort((a, b) => {
    if (a.offsetMinutes !== b.offsetMinutes) {
      return a.offsetMinutes - b.offsetMinutes
    }
    return a.label.localeCompare(b.label)
  })

  return [...popularWithDivider, ...sorted.map(({ offsetMinutes: _, ...rest }) => rest)]
}
